import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { NotificationsService } from './notifications.service';
import { Notification } from './notifications.types';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { SseService } from 'app/core/api-services/sse.service';
import { NotificationApiService } from 'app/core/api-services/notification-api.service';
import { UserService } from 'app/core/user/user.service';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    standalone: true,
    styleUrls: ['./notifications.component.scss'],
    imports: [
        MatButtonModule,
        TooltipModule,
        MatIconModule,
        MatTooltipModule,
        NgClass,
        NgTemplateOutlet,
        RouterLink,
        DatePipe,
    ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel')
    private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[];
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private sseSubscription!: Subscription;
    isMocked: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private sseService: SseService,
        private userService: UserService,
        private notificationService: NotificationApiService
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        const userData = this.userService.userData
        this.notifications = [];
        setTimeout(() => {
            const checkUserRole = setInterval(() => {
                if (userData.idRole) {
                    // Inizializza la connessione SSE
                    this.sseService.connect();

                    clearInterval(checkUserRole); // Interrompi il polling una volta trovato
                }
            }, 1000);
        }, 200);

        // Ascolta le notifiche SSE
        this.sseService.listenForNotifications((data) => {

            if (Array.isArray(data) && data.length > 0) {
                const newNotifications = data
                    .map(event => this._convertToNotification(event))
                    .filter(newNotification =>
                        !this.notifications.some(existing => existing.id === newNotification.id) // <-- Verifica duplicati
                    );

                if (newNotifications.length > 0) {

                    // Aggiungiamo solo le nuove notifiche
                    this.notifications = [...this.notifications, ...newNotifications];

                    // Ricalcoliamo gli unread
                    this._calculateUnreadCount();

                    // Forziamo il ricaricamento
                    this._changeDetectorRef.markForCheck();
                }
            }
        });
    }


    private _convertToNotification(event: any): Notification {
        return {
            id: event.idActiveEvent?.toString(),
            title: event.eventTopic || 'Nuova notifica',
            description: event.eventMsg || '',
            time: event.whenTimestamp || new Date().toISOString(),
            read: false, // Nuova notifica = non letta
        };
    }

    private _extractLink(eventMsg: string): string | undefined {
        const match = eventMsg.match(/href="([^"]+)"/);
        return match ? match[1] : undefined;
    }


    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(
            new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
        );
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
        const notificationIds = this.notifications.map(n => n.id); // Estrai gli ID delle notifiche

        if (notificationIds.length === 0) return; // Se non ci sono notifiche, esce

        this.notificationService.deleteMultipleNotifications(notificationIds).subscribe({
            next: () => {
                this.notifications = []; // Svuota l'array dopo la conferma del backend
                this._calculateUnreadCount(); // Ricalcola il numero di notifiche non lette
                this._changeDetectorRef.markForCheck(); // Aggiorna il componente

                console.log("Tutte le notifiche sono state marcate come lette ed eliminate");
            },
            error: (err) => {
                console.error("Errore durante l'eliminazione delle notifiche:", err);
            }
        });
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: Notification): void {
        // Toggle the read status
        notification.read = !notification.read;

        // Update the notification
        this._notificationsService
            .update(notification.id, notification)
            .subscribe();
    }

    getPlainText(html: string): string {
        return html ? html.replace(/<[^>]+>/g, '') : '';
    }

    /**
     * Delete the given notification
     */
    delete(notification: Notification): void {
        console.log(notification, "NOTIFICATIONDELETE");

        const notificationIds = [notification.id]; // Passiamo un array con un solo ID

        this.notificationService.deleteMultipleNotifications(notificationIds).subscribe({
            next: () => {
                // Rimuove la notifica dalla lista locale solo dopo la conferma del backend
                this.notifications = this.notifications.filter(n => n.id !== notification.id);

                // Ricalcola il numero di notifiche non lette
                this._calculateUnreadCount();

                // Aggiorna il componente
                this._changeDetectorRef.markForCheck();

                console.log(`Notifica con ID ${notification.id} eliminata con successo`);
            },
            error: (err) => {
                console.error("Errore durante l'eliminazione della notifica:", err);
            }
        });
    }



    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._notificationsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void {
        let count = 0;

        if (this.notifications && this.notifications.length) {
            count = this.notifications.filter(
                (notification) => !notification.read
            ).length;
        }

        this.unreadCount = count;
    }
}
